/**
 * This file contains the basic filter definitions for the frontend Algolia filters.
 * If you update it, you need to also update frontend-filter-objects.ts,
 * which is used by the frontend to render the filters.
 * These need to be in separate files because the ContentfulController seems to have issues
 * importing the instantsearch.js widgets.
 */

import { FilterData } from './algolia-types';

// NOTE: You need to add to the filterObjects object in frontend-filter-objects.ts when adding a new filter here
export const filterOptions: {
  [key: string]: FilterData;
} = {
  availableOnline: {
    title: 'Available Online',
    attribute: 'named_tags.available',
    id: 'availableOnline',
    widgetType: 'toggle',
  },
  category: {
    title: 'Product Type',
    attribute: 'named_tags.category',
    id: 'category',
    widgetType: 'refinementList',
  },
  collection: {
    title: 'Collection',
    attribute: 'named_tags.collection',
    id: 'collection',
    widgetType: 'refinementList',
  },
  gender: {
    title: 'Gender',
    attribute: 'named_tags.gender',
    id: 'gender',
    widgetType: 'refinementList',
  },
  brand: {
    title: 'Brand',
    attribute: 'named_tags.brand',
    id: 'brand',
    limit: 50,
    showMore: true,
    widgetType: 'refinementList',
  },
  labGrown: {
    title: 'Lab Grown',
    attribute: 'named_tags.labGrown',
    id: 'labGrown',
    widgetType: 'toggle',
  },
  gemstone: {
    title: 'Gemstone',
    attribute: 'named_tags.gemstone',
    id: 'gemstone',
    widgetType: 'refinementList',
  },
  gemstoneShape: {
    title: 'Gemstone Shape',
    attribute: 'named_tags.gemstoneShape',
    id: 'gemstoneShape',
    widgetType: 'refinementList',
  },
  diamondSettings: {
    title: 'Diamond Settings',
    attribute: 'named_tags.diamondSettings',
    id: 'diamondSettings',
    widgetType: 'refinementList',
  },
  diamondWeight: {
    title: 'Diamond Weight',
    attribute: 'named_tags.overallDiamondWeight',
    id: 'diamondWeight',
    widgetType: 'range',
  },
  band: {
    title: 'Band',
    attribute: 'named_tags.band',
    id: 'baand',
    widgetType: 'refinementList',
  },
  caseMaterial: {
    title: 'Case Material',
    attribute: 'named_tags.caseMaterial',
    id: 'caseMaterial',
    widgetType: 'refinementList',
  },
  caseSize: {
    title: 'Case Size',
    attribute: 'named_tags.caseSize',
    id: 'caseSize',
    widgetType: 'refinementList',
  },
  centreStoneShape: {
    title: 'Centre Stone Shape',
    attribute: 'named_tags.centreStoneShape',
    id: 'centreStoneShape',
    widgetType: 'refinementList',
  },
  customization: {
    title: 'Customization',
    attribute: 'named_tags.customization',
    id: 'customization',
    widgetType: 'refinementList',
  },
  dialColour: {
    title: 'Dial Colour',
    attribute: 'named_tags.dialColour',
    id: 'dialColour',
    widgetType: 'refinementList',
  },
  diameter: {
    title: 'Diameter',
    attribute: 'named_tags.diameter',
    id: 'diameter',
    widgetType: 'refinementList',
  },
  durability: {
    title: 'Durability',
    attribute: 'named_tags.durability',
    id: 'durability',
    widgetType: 'refinementList',
  },
  features: {
    title: 'Features',
    attribute: 'named_tags.features',
    id: 'features',
    widgetType: 'refinementList',
  },
  length: {
    title: 'Length',
    attribute: 'named_tags.length',
    id: 'length',
    widgetType: 'refinementList',
  },
  material: {
    title: 'Material',
    attribute: 'named_tags.material',
    id: 'material',
    widgetType: 'refinementList',
  },
  metalColour: {
    title: 'Metal Colour',
    attribute: 'named_tags.metalColour',
    id: 'metalColour',
    widgetType: 'refinementList',
  },
  metalType: {
    title: 'Metal Type',
    attribute: 'named_tags.metalType',
    id: 'metalType',
    widgetType: 'refinementList',
  },
  movement: {
    title: 'Movement',
    attribute: 'named_tags.movement',
    id: 'movement',
    widgetType: 'refinementList',
  },
  primaryStone: {
    title: 'Primary Stone',
    attribute: 'named_tags.primaryStone',
    id: 'primaryStone',
    widgetType: 'refinementList',
  },
  style: {
    title: 'Style',
    attribute: 'named_tags.style',
    id: 'style',
    widgetType: 'refinementList',
  },
  waterResistance: {
    title: 'Water Resistance',
    attribute: 'named_tags.waterResistance',
    id: 'waterResistance',
    widgetType: 'refinementList',
  },
  powerReserve: {
    title: 'Power Reserve',
    attribute: 'named_tags.powerReserve',
    id: 'powerReserve',
    widgetType: 'refinementList',
  },
  earringStyle: {
    title: 'Earring Style',
    attribute: 'named_tags.earringStyle',
    id: 'earringStyle',
    widgetType: 'refinementList',
  },
  braceletStyle: {
    title: 'Bracelet Style',
    attribute: 'named_tags.braceletStyle',
    id: 'braceletStyle',
    widgetType: 'refinementList',
  },
  bandType: {
    title: 'Band Type',
    attribute: 'named_tags.bandType',
    id: 'bandType',
    widgetType: 'refinementList',
  },
  giaCertified: {
    title: 'GIA Certified',
    attribute: 'named_tags.GIAcertified',
    id: 'giaCertified',
    widgetType: 'toggle',
  },
  limitedEdition: {
    title: 'Limited Edition',
    attribute: 'named_tags.limitedEdition',
    id: 'limitedEdition',
    widgetType: 'toggle',
  },
  newArrival: {
    title: 'New Arrivals',
    attribute: 'named_tags.newArrival',
    id: 'newArrival',
    widgetType: 'toggle',
  },
};

/** Return all filters except for specific ones */
function allFilterOptionsExcept(filterIds: string[]) {
  return Object.values(filterOptions).filter((filter) => !filterIds.includes(filter.id));
}

/** Map of the filter options to use for each collection, by handle */
const collectionFilterOptions: Record<string, FilterData[]> = {
  // Just show all by default
  default: [...Object.values(filterOptions)],
  'timepieces-tudor': [...Object.values(filterOptions)],

  // Special collections (and their subcategories)
  'timepieces': allFilterOptionsExcept(['labGrown', 'diamondWeight', 'giaCertified']),
  'jewellery': allFilterOptionsExcept(['limitedEdition']),
  'wedding-engagement': allFilterOptionsExcept(['limitedEdition', 'availableOnline']),
  'gifts-accessories': allFilterOptionsExcept(['labGrown', 'diamondWeight', 'giaCertified']),
};

/**
 * Get the identifier to use for grabbing the filter options
 * @param collectionHandle Handle of the collection we're getting filters for
 * @param slug Technically not the slug, technically the "name" of the collection page in contentful, I believe,
 *   But it's got the parent collection in it, which is what I need to make the children match the parent
 * @returns The identifier to use for the filter options
 */
export function getFilterSetIdentifier(collectionHandle: string, slug?: string) {
  // If there is a specific set for the collection handle, use the handle
  if (collectionFilterOptions[collectionHandle]) {
    return collectionHandle;
  } else if (
    // Check collection categories, essentially
    collectionHandle === 'new-old-stock'
    || slug?.indexOf('timepieces') !== -1
  ) {
    return 'timepieces';
  } else if (slug?.indexOf('jewellery') !== -1) {
    return 'jewellery';
  } else if (
    (slug?.indexOf('wedding') !== -1)
    || (slug?.indexOf('engagement') !== -1)
  ) {
    return 'wedding-engagement';
  } else if (slug?.indexOf('gifts-accessories') !== -1) {
    return 'gifts-accessories';
  }

  return 'default';
}

/** Get the filter options to use for a given collection handle */
export default function getFilterOptions(collectionHandle: string, slug?: string): FilterData[] {
  const filterIdentifier = getFilterSetIdentifier(collectionHandle, slug);
  // console.debug('Filter options identifier:', filterIdentifier);
  const filters = collectionFilterOptions[filterIdentifier];
  if (!filters) {
    return [];
  }
  return filters;
}
